import {useQuery} from '@apollo/client';
import {FinancialCurrencyCard} from './FinancialCards';
import {OrderMetricsCard} from './OrderMetricsCard';
import {GET_ORDER_METRICS} from 'graphql/queries/financial';
import {Form} from 'antd';
import {useState} from 'react';
import styles from './financial.module.scss';
import {MetricsFilterBar} from './MetricsFilterBar';
import moment from 'moment';
import {ExportFinancialButton} from './ExportFinancialButton';
import {currentDayOfMonth, firstDayOfMonth} from './WalletMetrics';
import {NavTabsEnum} from 'utils/constants';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';

export const OrderMetrics = () => {
  const [form] = Form.useForm();
  const startDate = Form.useWatch('startDate', form);
  const endDate = Form.useWatch('endDate', form);
  const allTime = Form.useWatch('allTime', form);
  const status = Form.useWatch('status', form);

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.Financials),
  );

  const [data, setData] = useState({});
  const {loading, refetch} = useQuery(GET_ORDER_METRICS, {
    variables: {
      filters: {
        date: {
          range: 'MONTHLY',
        },
      },
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => setData(data?.getOrderMetrics?.data),
  });

  return (
    <>
      <MetricsFilterBar
        form={form}
        loading={loading}
        initialValues={{
          startDate: firstDayOfMonth,
          endDate: currentDayOfMonth,
        }}
        actions={
          tabOperations?.export ? (
            <ExportFinancialButton
              startDate={startDate}
              endDate={endDate}
              allTime={allTime}
              status={status}
              query={GET_ORDER_METRICS}
              type="Order"
            />
          ) : null
        }
        onFinish={values => {
          if (values?.allTime) {
            return refetch({
              'filters': {
                'date': {
                  'range': 'MONTHLY',
                },
                ...(values?.status && {status: values?.status}),
                'allTime': true,
              },
            });
          }
          if (!values?.startDate && !values?.endDate) {
            return refetch({
              filters: {
                ...(values?.status && {status: values?.status}),
                date: {
                  range: 'MONTHLY',
                },
              },
            });
          }
          refetch({
            filters: {
              ...(values?.status && {status: values?.status}),
              date: {
                ...(values?.startDate && {
                  startDate: moment(values?.startDate).format(),
                }),
                ...(values?.endDate && {
                  endDate: moment(values?.endDate).format(),
                }),
              },
            },
          });
        }}
      />
      <div className={styles.metricCardsRow}>
        {/* Handle new data structure with items array */}
        {data?.stocks && Array.isArray(data.stocks) && (
          <OrderMetricsCard
            title="STOCKS"
            items={data.stocks}
            loading={loading}
          />
        )}
        {data?.bonds && Array.isArray(data.bonds) && (
          <OrderMetricsCard
            title="BONDS"
            items={data.bonds}
            loading={loading}
          />
        )}
        {data?.mutualFunds && Array.isArray(data.mutualFunds) && (
          <OrderMetricsCard
            title="MUTUAL FUNDS"
            items={data.mutualFunds}
            loading={loading}
          />
        )}
        {data?.others && Array.isArray(data.others) && (
          <OrderMetricsCard
            title="OTHER ASSETS"
            items={data.others}
            loading={loading}
          />
        )}

        {/* Fallback to old structure if new structure is not available */}
        {!Array.isArray(data?.stocks) && data?.stocks && (
          <div className={styles.metricCardBox}>
            <h2 className={styles.sectionTitle}>STOCKS</h2>
            <div className={styles.metricCardList}>
              {loading
                ? Array(3)
                    .fill('')
                    .map((data, key) => (
                      <div key={key} className={styles.loadingSkeleton}>
                        {data}
                      </div>
                    ))
                : data?.stocks?.map((data, key) => (
                    <FinancialCurrencyCard
                      type={data?.currency}
                      key={key}
                      currency={data?.type}
                      amount={data?.total}
                    />
                  ))}
            </div>
          </div>
        )}
        {!Array.isArray(data?.bonds) && data?.bonds && (
          <div className={styles.metricCardBox}>
            <h2 className={styles.sectionTitle}>BONDS</h2>
            <div className={styles.metricCardList}>
              {loading
                ? Array(3)
                    .fill('')
                    .map((data, key) => (
                      <div key={key} className={styles.loadingSkeleton}>
                        {data}
                      </div>
                    ))
                : data?.bonds?.map((data, key) => (
                    <FinancialCurrencyCard
                      type={data?.currency}
                      key={key}
                      currency={data?.type}
                      amount={data?.total}
                    />
                  ))}
            </div>
          </div>
        )}
        {!Array.isArray(data?.mutualFunds) && data?.mutualFunds && (
          <div className={styles.metricCardBox}>
            <h2 className={styles.sectionTitle}>MUTUAL FUNDS</h2>
            <div className={styles.metricCardList}>
              {loading
                ? Array(3)
                    .fill('')
                    .map((data, key) => (
                      <div key={key} className={styles.loadingSkeleton}>
                        {data}
                      </div>
                    ))
                : data?.mutualFunds?.map((data, key) => (
                    <FinancialCurrencyCard
                      type={data?.currency}
                      key={key}
                      currency={data?.type}
                      amount={data?.total}
                    />
                  ))}
            </div>
          </div>
        )}
        {!Array.isArray(data?.others) && !!data?.others?.length && (
          <div className={styles.metricCardBox}>
            <h2 className={styles.sectionTitle}>OTHER ASSETS</h2>
            <div className={styles.metricCardList}>
              {loading
                ? Array(3)
                    .fill('')
                    .map((data, key) => (
                      <div key={key} className={styles.loadingSkeleton}>
                        {data}
                      </div>
                    ))
                : data?.others?.map((data, key) => (
                    <FinancialCurrencyCard
                      type={data?.currency}
                      key={key}
                      currency={data?.type}
                      amount={data?.total}
                    />
                  ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
